import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { CardAccountCard, CardCard, CardProgramCard, CopyInput, Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, FormElement, FormLabel, FormText, Inner } from '~/styles';
import { formatDateTime, formatString } from '~/util';

import { CardTokenProps } from './CardToken';

interface Params {
  id: string;
}

export const PageCardTokenInformation: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { cardToken: outletCardToken } = useOutletContext<CardTokenProps>();
  const { currentPlatform, isSandbox } = useSessionStore();

  const { data: cardToken, error: cardTokenError } = useQuery({
    queryKey: ['card-token', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardTokenRepository.get(id!),
    enabled: !!id && !outletCardToken,
    initialData: outletCardToken,
  });

  useEffect(() => {
    if (cardTokenError) {
      navigate(ROUTE.CARD_TOKENS);
      addDangerNotification({
        content: (cardTokenError as any).message || 'Failed to load card token',
      });
    }
  }, [cardTokenError, navigate, addDangerNotification]);

  if (!cardToken) {
    return null;
  }

  return (
    <Inner p={0}>
      <Section headline="Information">
        <Inner px="var(--page-margin)" py={16}>
          <Flex flexDirection="column" gap={16}>
            <FormElement>
              <FormLabel>Card Token ID</FormLabel>
              <CopyInput value={cardToken.id} isMonospaced />
            </FormElement>

            {cardToken.cardId && (
              <FormElement>
                <FormLabel>Card</FormLabel>
                <CardCard cardId={cardToken.cardId} />
              </FormElement>
            )}

            {cardToken.cardAccountId && (
              <FormElement>
                <FormLabel>Card Account</FormLabel>
                <CardAccountCard cardAccountId={cardToken.cardAccountId} />
              </FormElement>
            )}

            {cardToken.cardProgramId && (
              <FormElement>
                <FormLabel>Card Program</FormLabel>
                <CardProgramCard cardProgramId={cardToken.cardProgramId} />
              </FormElement>
            )}

            <FormElement>
              <FormLabel>Status</FormLabel>
              <FormText>
                <Chip>{formatString(cardToken.status || '')}</Chip>
              </FormText>
            </FormElement>

            <FormElement>
              <FormLabel>Wallet Type</FormLabel>
              <FormText>
                <Chip>{formatString(cardToken.walletType || '')}</Chip>
              </FormText>
            </FormElement>

            {cardToken.tokenType && (
              <FormElement>
                <FormLabel>Token Type</FormLabel>
                <FormText>
                  <Chip>{formatString(cardToken.tokenType)}</Chip>
                </FormText>
              </FormElement>
            )}

            {cardToken.deviceType && (
              <FormElement>
                <FormLabel>Device Type</FormLabel>
                <FormText>
                  <Chip>{formatString(cardToken.deviceType)}</Chip>
                </FormText>
              </FormElement>
            )}

            {cardToken.deviceName && (
              <FormElement>
                <FormLabel>Device Name</FormLabel>
                <CopyInput value={cardToken.deviceName} />
              </FormElement>
            )}

            {cardToken.requestorName && (
              <FormElement>
                <FormLabel>Requestor Name</FormLabel>
                <CopyInput value={cardToken.requestorName} />
              </FormElement>
            )}

            {cardToken.createdAt && (
              <FormElement>
                <FormLabel>Created At</FormLabel>
                <CopyInput value={formatDateTime(cardToken.createdAt, { timeZoneFormat: 'utc' })} />
              </FormElement>
            )}
          </Flex>
        </Inner>
      </Section>
    </Inner>
  );
};
